import type { CmsContentAboutServicesForm } from "@/modules/cms/domain/contracts";
import {
  ServiceItemSourceSelector,
  type ServiceItemSource,
} from "@/modules/cms/presentation/ui/forms/ServiceItemSourceSelector";
import { StockServicePicker } from "@/modules/cms/presentation/ui/forms/StockServicePicker";
import { AppImage } from "@/shared/ui/AppImage";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import {
  Badge,
  Button,
  FormCheckbox,
  FormInput,
  FormNumber,
  FormTextarea,
  Label,
  RHF,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from "@devhop/ui";
import {
  ChevronDown,
  Database,
  GripVertical,
  ImageIcon,
  PenLine,
  TrashIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

const IMAGE_KEY_PREFIX = "uploads/cms/about-services";

const LINK_MODE_OPTIONS = [
  { value: "none" as const, label: "ບໍ່ມີລິ້ງ" },
  { value: "showcase" as const, label: "ໜ້າ Showcase" },
  { value: "product" as const, label: "ໜ້າສິນຄ້າ (ຮ້ານ)" },
];

const SHOWCASE_KIND_OPTIONS = [
  { value: "production" as const, label: "ແບບຮັບຜະລິດ" },
  { value: "online-showcase" as const, label: "ແບບສິນຄ້າອອນໄລນ໌" },
];

const LOCALE_TABS = [
  { value: "lo" as const, label: "ລາວ" },
  { value: "th" as const, label: "ໄທ" },
  { value: "en" as const, label: "EN" },
];

type AboutServiceItemCardProps = {
  index: number;
  source: ServiceItemSource;
  isOpen: boolean;
  onToggle: () => void;
  onSourceChange: (source: ServiceItemSource) => void;
  onRemove: () => void;
  methods: UseFormReturn<CmsContentAboutServicesForm>;
  pendingFile: File | null;
  onPendingFileChange: (file: File | null) => void;
};

function SectionBlock({
  title,
  description,
  children,
  className,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("rounded-lg border bg-muted/20 p-4", className)}>
      <div className="mb-3">
        <h4 className="font-medium text-sm">{title}</h4>
        {description ? (
          <p className="mt-0.5 text-muted-foreground text-xs">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export function AboutServiceItemCard({
  index,
  source,
  isOpen,
  onToggle,
  onSourceChange,
  onRemove,
  methods,
  pendingFile,
  onPendingFileChange,
}: AboutServiceItemCardProps) {
  const [localeTab, setLocaleTab] = useState<"lo" | "th" | "en">("lo");

  const titleLo = methods.watch(`items.${index}.title.lo`);
  const titleEn = methods.watch(`items.${index}.title.en`);
  const slug = methods.watch(`items.${index}.slug`);
  const order = methods.watch(`items.${index}.order`);
  const isActive = methods.watch(`items.${index}.isActive`);
  const stockCode = methods.watch(`items.${index}.stockServiceCode`);
  const imageKey = methods.watch(`items.${index}.imageKey`) ?? "";
  const linkMode = methods.watch(`items.${index}.linkMode`);

  const stockPending = source === "stock" && !stockCode?.trim();
  const displayTitle = titleEn || titleLo || `ບໍລິການ ${index + 1}`;

  const pendingPreviewUrl = useMemo(
    () => (pendingFile ? URL.createObjectURL(pendingFile) : null),
    [pendingFile],
  );

  useEffect(
    () => () => {
      if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
    },
    [pendingPreviewUrl],
  );

  const headerThumbSrc = pendingPreviewUrl ?? (imageKey || null);

  return (
    <article
      className={cn(
        "overflow-hidden rounded-lg border bg-card shadow-sm transition-shadow",
        isOpen && "ring-1 ring-primary/20",
        !isActive && "opacity-75",
      )}
    >
      <div className="flex items-stretch gap-0">
        <div className="hidden w-8 shrink-0 items-center justify-center border-r bg-muted/30 text-muted-foreground sm:flex">
          <GripVertical className="size-4" aria-hidden />
        </div>

        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={onToggle}
            className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/30 sm:px-4"
          >
            <div className="size-12 shrink-0 overflow-hidden rounded-md border bg-muted/40">
              {headerThumbSrc ? (
                pendingPreviewUrl ? (
                  <img
                    src={pendingPreviewUrl}
                    alt=""
                    className="size-full object-cover"
                  />
                ) : (
                  <AppImage
                    src={headerThumbSrc}
                    alt={displayTitle}
                    className="size-full"
                    showLoading={false}
                  />
                )
              ) : (
                <div className="flex size-full items-center justify-center text-muted-foreground">
                  <ImageIcon className="size-5" />
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate font-medium text-sm">{displayTitle}</span>
                <Badge
                  variant="outline"
                  className={cn(
                    "gap-1 text-xs",
                    source === "stock"
                      ? "border-primary/40 text-primary"
                      : "text-muted-foreground",
                  )}
                >
                  {source === "stock" ? (
                    <Database className="size-3" />
                  ) : (
                    <PenLine className="size-3" />
                  )}
                  {source === "stock" ? "Stock POS" : "CMS"}
                </Badge>
                {!isActive && (
                  <Badge variant="secondary" className="text-xs">
                    ປິດ
                  </Badge>
                )}
                {stockPending && (
                  <Badge variant="outline" className="border-amber-500 text-amber-700 text-xs">
                    ລໍຖ້າເລືອກ Stock
                  </Badge>
                )}
              </div>
              <p className="mt-0.5 truncate text-muted-foreground text-xs">
                {slug ? `/${slug}` : "— slug —"}
                {stockCode ? ` · ${stockCode}` : ""}
                {typeof order === "number" ? ` · ລຳດັບ ${order}` : ""}
              </p>
            </div>

            <ChevronDown
              className={cn(
                "size-4 shrink-0 text-muted-foreground transition-transform",
                isOpen && "rotate-180",
              )}
            />
          </button>

          {isOpen && (
            <div className="space-y-4 border-t px-3 pb-4 pt-4 sm:px-4">
              <input type="hidden" {...methods.register(`items.${index}.id`)} />

              <SectionBlock
                title="ແຫຼ່ງຂໍ້ມູນ"
                description={
                  source === "stock"
                    ? "ເລືອກບໍລິການ Stock — ລະບົບເຕີມຫົວຂໍ້, slug, ຮູບ ແລະເນື້ອຫາໃຫ້"
                    : "ກຳນົດເນື້ອຫາ ແລະເຊື່ອມໜ້າ Showcase/ສິນຄ້າເອງ"
                }
              >
                <ServiceItemSourceSelector
                  value={source}
                  onChange={onSourceChange}
                />
                {source === "stock" && (
                  <div className="mt-4">
                    <StockServicePicker
                      control={methods.control}
                      setValue={methods.setValue}
                      serviceIndex={index}
                      pendingSelection={stockPending}
                    />
                  </div>
                )}
              </SectionBlock>

              {!stockPending && (
                <>
                  <SectionBlock title="ຕັ້ງຄ່າທົ່ວໄປ">
                    <div className="flex flex-wrap items-end gap-3">
                      <FormInput
                        name={`items.${index}.slug`}
                        label="Slug (URL)"
                        className="min-w-45 flex-1"
                      />
                      <FormNumber
                        name={`items.${index}.order`}
                        label="ລຳດັບ"
                        min={0}
                        className="w-24"
                      />
                      <FormCheckbox
                        name={`items.${index}.isActive`}
                        label="ເປີດໃຊ້ງານ"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onRemove}
                        className="text-destructive hover:text-destructive"
                      >
                        <TrashIcon className="size-4" />
                        ລຶບ
                      </Button>
                    </div>
                  </SectionBlock>

                  {source === "manual" && (
                    <SectionBlock title="ຮູບປົກ" description="ອັດສ່ວນ 1:1 · 400×400 px">
                      <ImageKeyUploadField
                        keyPrefix={IMAGE_KEY_PREFIX}
                        label="ຮູບ"
                        aspectHint="ອັດສ່ວນ 1:1"
                        aspectRatio="aspect-square"
                        widthPx={400}
                        heightPx={400}
                        className="max-w-45"
                        value={imageKey}
                        onChange={(key) =>
                          methods.setValue(`items.${index}.imageKey`, key, {
                            shouldDirty: true,
                          })
                        }
                        deferUpload
                        pendingFile={pendingFile}
                        onFileSelect={onPendingFileChange}
                        onClear={() => {
                          methods.setValue(`items.${index}.imageKey`, "", {
                            shouldDirty: true,
                          });
                          onPendingFileChange(null);
                        }}
                      />
                    </SectionBlock>
                  )}

                  {source === "stock" && imageKey && (
                    <SectionBlock
                      title="ຮູບ (ຈາກ Stock POS)"
                      description="ດຶງອັດຕະໂນມັດ — ບໍ່ສາມາດອັບໂຫຼດແທນໄດ້"
                    >
                      <AppImage
                        src={imageKey}
                        alt={displayTitle}
                        className="aspect-square max-w-45 rounded-md border"
                        fallback={
                          <div className="flex aspect-square max-w-45 items-center justify-center rounded-md border border-dashed bg-muted/40 p-3 text-center text-muted-foreground text-xs">
                            ບໍລິການ Stock ຍັງບໍ່ມີຮູບ
                          </div>
                        }
                      />
                    </SectionBlock>
                  )}

                  <SectionBlock
                    title="ເນື້ອຫາ"
                    description="ຫົວຂໍ້ · ຄຳອະທິບາຍສັ້ນ · Intro ຫນ້າບໍລິການ"
                  >
                    <Tabs
                      value={localeTab}
                      onValueChange={(v) => setLocaleTab(v as "lo" | "th" | "en")}
                    >
                      <TabsList className="grid w-full max-w-xs grid-cols-3">
                        {LOCALE_TABS.map((tab) => (
                          <TabsTrigger key={tab.value} value={tab.value}>
                            {tab.label}
                          </TabsTrigger>
                        ))}
                      </TabsList>

                      {LOCALE_TABS.map((tab) => (
                        <TabsContent
                          key={tab.value}
                          value={tab.value}
                          className="mt-4 space-y-3"
                        >
                          <FormInput
                            name={`items.${index}.title.${tab.value}`}
                            label={`ຫົວຂໍ້ (${tab.label})`}
                          />
                          <FormTextarea
                            name={`items.${index}.content.${tab.value}`}
                            label={`ເນື້ອຫາສັ້ນ (${tab.label})`}
                            rows={2}
                          />
                          <FormTextarea
                            name={`items.${index}.intro.${tab.value}`}
                            label={`Intro ຫນ້າບໍລິການ (${tab.label})`}
                            rows={2}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                  </SectionBlock>

                  {source !== "stock" && (
                    <SectionBlock
                      title="ເຊື່ອມໄປຫນ້າ"
                      description="Showcase = ໜ້າລາຍການແບບຕົວຢ່າງ · Product = ໜ້າສິນຄ້າຮ້ານ"
                    >
                      <div className="grid gap-4 sm:grid-cols-2">
                        <RHF.Controller
                          control={methods.control}
                          name={`items.${index}.linkMode`}
                          render={({ field }) => (
                            <div className="space-y-2">
                              <Label>ປະເພດລິ້ງ</Label>
                              <Select
                                value={field.value ?? "none"}
                                onValueChange={field.onChange}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {LINK_MODE_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        />
                        {linkMode === "showcase" && (
                          <RHF.Controller
                            control={methods.control}
                            name={`items.${index}.showcaseKind`}
                            render={({ field }) => (
                              <div className="space-y-2">
                                <Label>ປະເພດ Showcase</Label>
                                <Select
                                  value={field.value ?? "production"}
                                  onValueChange={field.onChange}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {SHOWCASE_KIND_OPTIONS.map((opt) => (
                                      <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          />
                        )}
                      </div>
                    </SectionBlock>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
